import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Layout from '../components/Layout';
import { CREATE_AD } from '../graphql/mutations';
import { GET_CATEGORIES } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { Form, Button, Alert, Card, Row, Col, Container } from 'react-bootstrap';

const schema = yup.object({
  title: yup.string().required('Title is required').min(10, 'Title must be at least 10 characters'),
  description: yup.string().required('Description is required').min(200, 'Description must be at least 200 words'),
  categoryId: yup.string().required('Category is required'),
  priceType: yup.string().required('Price type is required'),
  price: yup.number().when('priceType', {
    is: 'fixed',
    then: (schema) => schema.required('Price is required').min(0, 'Price must be positive'),
    otherwise: (schema) => schema.optional().nullable().transform(() => undefined),
  }),
  condition: yup.string().required('Condition is required'),
  location: yup.string().required('Location is required'),
  city: yup.string().required('City is required'),
  deliveryMethod: yup.string().required('Delivery method is required'),
}).required();

const PostAd: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [images, setImages] = useState<string[]>([]);
  const [video, setVideo] = useState<string>('');
  const [videoPreview, setVideoPreview] = useState<string>('');
  const [error, setError] = useState('');

  const { data: categoriesData } = useQuery(GET_CATEGORIES);
  const [createAd, { loading }] = useMutation(CREATE_AD);
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      priceType: 'fixed',
      condition: 'good',
      deliveryMethod: 'pickup',
    }
  });

  const priceType = watch('priceType');

  if (!isAuthenticated) {
    return (
      <Layout showSidebar={false}>
        <Container className="py-5">
          <div className="text-center mx-auto" style={{ maxWidth: '600px' }}>
            <h1 className="mb-4">Please Login</h1>
            <p className="text-muted mb-4">You need to be logged in to post an ad.</p>
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              size="lg"
            >
              Login
            </Button>
          </div>
        </Container>
      </Layout>
    );
  }

  const onSubmit = async (data: any) => {
    try {
      setError('');
      const result = await createAd({
        variables: {
          input: {
            ...data,
            price: data.price ? parseFloat(data.price) : null,
            images: images.length > 0 ? images : undefined,
            video: video || undefined,
          }
        }
      });
      
      if (result.data?.createAd?.success) {
        navigate('/my-ads');
      } else {
        setError(result.data?.createAd?.message || 'Failed to create ad');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).slice(0, 5 - images.length).forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImages(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match('video.*')) {
      setError('Please select a video file');
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setError('File size must be less than 100MB');
      return;
    }

    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setVideoPreview(base64String);
      setVideo(base64String);
    };
    reader.readAsDataURL(file);
  };

  const removeVideo = () => {
    setVideo('');
    setVideoPreview('');
  };

  const categories = categoriesData?.rootCategories || [];

  return (
    <Layout showSidebar={false}>
      <Container className="py-4">
        <div className="mx-auto pw-page" style={{ maxWidth: '800px' }}>
          <h1 className="mb-2 pw-title">Post an Ad</h1>
          <p className="pw-muted mb-4">Fill in the details below to create your listing</p>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Card className="pw-card">
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit(onSubmit)} className="pw-form">
                {/* Title */}
                <Form.Group className="mb-4">
                  <Form.Label>Ad Title *</Form.Label>
                  <Form.Control
                    {...register('title')}
                    type="text"
                    placeholder="e.g., iPhone 14 Pro Max 256GB - Like New"
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Category */}
                <Form.Group className="mb-4">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    {...register('categoryId')}
                    isInvalid={!!errors.categoryId}
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat: any) => (
                      <optgroup key={cat.id} label={cat.name}>
                        <option value={cat.id}>{cat.name}</option>
                        {cat.childrenList?.map((child: any) => (
                          <option key={child.id} value={child.id}>
                            &nbsp;&nbsp;{child.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.categoryId?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Price */}
                <Form.Group className="mb-4">
                  <Form.Label>Price *</Form.Label>
                  <Row>
                    <Col md={priceType === 'fixed' ? 5 : 12}>
                      <Form.Select
                        {...register('priceType')}
                      >
                        <option value="fixed">Fixed Price</option>
                        <option value="negotiable">Negotiable</option>
                        <option value="free">Free</option>
                        <option value="swap">Swap/Trade</option>
                      </Form.Select>
                    </Col>
                    {priceType === 'fixed' && (
                      <Col md={7}>
                        <Form.Control
                          {...register('price')}
                          type="number"
                          placeholder="Price in NPR"
                          isInvalid={!!errors.price}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.price?.message}
                        </Form.Control.Feedback>
                      </Col>
                    )}
                  </Row>
                </Form.Group>

                {/* Condition */}
                <Form.Group className="mb-4">
                  <Form.Label>Condition *</Form.Label>
                  <Form.Select
                    {...register('condition')}
                  >
                    <option value="new">New</option>
                    <option value="like_new">Like New</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </Form.Select>
                </Form.Group>

                {/* Description */}
                <Form.Group className="mb-4">
                  <Form.Label>Description * (min 200 words)</Form.Label>
                  <Form.Control
                    {...register('description')}
                    as="textarea"
                    rows={6}
                    placeholder="Describe your item in detail..."
                    isInvalid={!!errors.description}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Location */}
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Location *</Form.Label>
                      <Form.Control
                        {...register('location')}
                        type="text"
                        placeholder="e.g., New Road"
                        isInvalid={!!errors.location}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.location?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>City *</Form.Label>
                      <Form.Control
                        {...register('city')}
                        type="text"
                        placeholder="e.g., Kathmandu"
                        isInvalid={!!errors.city}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.city?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Delivery Method */}
                <Form.Group className="mb-4">
                  <Form.Label>Delivery Method *</Form.Label>
                  <Form.Select
                    {...register('deliveryMethod')}
                  >
                    <option value="pickup">Pickup Only</option>
                    <option value="delivery">Delivery Available</option>
                    <option value="shipping">Shipping Available</option>
                    <option value="both">Pickup & Delivery</option>
                  </Form.Select>
                </Form.Group>

                {/* Images */}
                <Form.Group className="mb-4">
                  <Form.Label>Images (up to 5)</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                  />
                  {images.length > 0 && (
                    <div className="mt-3 d-flex gap-2">
                      {images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Preview ${idx}`}
                          className="rounded"
                          style={{ height: '80px', width: '80px', objectFit: 'cover' }}
                        />
                      ))}
                    </div>
                  )}
                </Form.Group>

                {/* Video */}
                <Form.Group className="mb-4">
                  <Form.Label>Video (Optional - for Story Ads)</Form.Label>
                  {!videoPreview ? (
                    <Form.Control
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                    />
                  ) : (
                    <div className="mt-3">
                      <video
                        src={videoPreview}
                        className="rounded"
                        style={{ width: '100%', maxWidth: '400px', height: '256px', objectFit: 'cover' }}
                        controls
                      />
                      <Button
                        variant="danger"
                        type="button"
                        onClick={removeVideo}
                        className="mt-2"
                        size="sm"
                      >
                        Remove Video
                      </Button>
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Add a video to make your ad appear in Stories. Max 100MB. Videos will be featured in the Stories tab.
                  </Form.Text>
                </Form.Group>

                {/* Submit */}
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="flex-grow-1"
                  >
                    {loading ? 'Creating...' : 'Post Ad'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </Layout>
  );
};

export default PostAd;
